import React, { createContext, useContext, useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { useCurrency } from './CurrencyContext';

type StripeContextType = {
  isLoading: boolean;
  error: string | null;
  clientSecret: string | null;
  setClientSecret: (secret: string | null) => void;
  paymentAmount: number | null;
  setPaymentAmount: (amount: number | null) => void;
};

const StripeContext = createContext<StripeContextType>({
  isLoading: true,
  error: null,
  clientSecret: null,
  setClientSecret: () => {},
  paymentAmount: null,
  setPaymentAmount: () => {},
});

export const useStripe = () => useContext(StripeContext);

// Get Stripe publishable key from environment variables
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

export const StripeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const { selectedCurrency } = useCurrency();

  useEffect(() => {
    // Load Stripe.js
    stripePromise
      .then(() => {
        setIsLoading(false);
      })
      .catch((err) => {
        setError('Failed to load payment system. Please try again later.');
        setIsLoading(false);
      });
  }, []);

  // Stripe elements options - use type assertion to workaround TypeScript issues with Stripe types
  const options = clientSecret
    ? {
        clientSecret,
        mode: 'payment' as const,
        currency: selectedCurrency.toLowerCase(),
        amount: paymentAmount || 100, // Minimum amount of $1.00 if no specific amount provided
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#00742f', // jamaica-green
          },
        },
      } as StripeElementsOptions
    : {
        mode: 'setup' as const,
        currency: selectedCurrency.toLowerCase(),
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#00742f', // jamaica-green
          },
        },
      } as StripeElementsOptions;

  return (
    <StripeContext.Provider value={{ 
      isLoading, 
      error, 
      clientSecret, 
      setClientSecret,
      paymentAmount,
      setPaymentAmount 
    }}>
      <Elements stripe={stripePromise} options={options}>
        {children}
      </Elements>
    </StripeContext.Provider>
  );
}; 